import { getConfig } from 'EnvConfig'

import { getEcpDomain } from './ecp-domain'
import { getPublicConfigRequestHeaders } from './request-headers'

export type BootstrapPortalConfig = {
  prevent_search_engine_indexing?: boolean
  is_dummy?: boolean
  organization_id?: string
  design_id?: string
  [key: string]: unknown
}

export class PublicPortalConfigError extends Error {
  readonly response: { status: number }

  constructor(status: number) {
    super(`Failed to fetch public portal config (${status})`)
    this.name = 'PublicPortalConfigError'
    this.response = { status }
  }
}

const requestPublicPortalConfig = async (): Promise<BootstrapPortalConfig> => {
  const domain = getEcpDomain()
  const { CUSTOMER_PORTAL_API_URL } = getConfig()
  const url = new URL(`${CUSTOMER_PORTAL_API_URL}/v2/portal/public/config`)

  url.searchParams.set('domain', domain)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getPublicConfigRequestHeaders()
  })

  if (!response.ok) {
    throw new PublicPortalConfigError(response.status)
  }

  return (await response.json()) as BootstrapPortalConfig
}

let primed: Promise<BootstrapPortalConfig> | undefined

// The boot entry starts this request before React mounts, then
// PortalConfigProvider asks for the same URL; the endpoint sends no-store, so
// without a handoff both go to the network, back to back.
export const primePublicPortalConfig = (): Promise<BootstrapPortalConfig> => {
  primed ??= requestPublicPortalConfig()

  return primed
}

export const fetchPublicPortalConfig = (): Promise<BootstrapPortalConfig> => {
  if (!primed) return requestPublicPortalConfig()

  const handoff = primed

  // Consumed once, so a PORTAL_SAVED_EVENT invalidation still hits the network.
  primed = undefined

  return handoff
}
